import { useEffect, useMemo, useState } from 'react'
import { IconSearch } from '@tabler/icons-react'
import clsx from 'clsx'

import {
  ALGOLIA_PRODUCTS_INDEX_NAME,
  ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
  getCurrentAlgoliaInsightsUserToken
} from 'lib/algolia'
import { algoliaInsightsHitsViewed } from 'lib/gtm'
import {
  getAlgoliaProductID,
  isUnavailableProduct,
  ProductTypes
} from 'modules/products'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import { isNilOrEmpty, isNotNilOrEmpty } from 'shared/utils/ramda'

import {
  SEARCH_QUERY_INSTANT_RESULTS_DISPLAY_COUNT,
  SEARCH_QUERY_SUGGESTIONS_DISPLAY_COUNT
} from '../config'
import { SearchDropdownMode, SearchQuerySuggestionItem } from '../types'

import SearchProductsResultsSkeleton from './skeletons/SearchProductsResultsSkeleton'
import SearchDropdownNoResults from './SearchDropdownNoResults'
import SearchProductCard from './SearchProductCard'
import SearchQuerySuggestions from './SearchQuerySuggestions'

import styles from './SearchDropdown.module.scss'

interface SearchDropdownProps {
  query: string
  productsQueryID?: string | null // Required for Algolia analytics
  customerId?: string
  isOpen: boolean
  dropdownMode: SearchDropdownMode
  productResults: ProductTypes[]
  querySuggestions: SearchQuerySuggestionItem[]
  isLoading: boolean
  handleSearchSubmit: (e?: React.FormEvent) => void
  isRibbonActive?: boolean
}

const SearchDropdown = ({
  query,
  productsQueryID = null,
  customerId,
  isOpen,
  productResults,
  querySuggestions,
  dropdownMode,
  isLoading,
  handleSearchSubmit,
  isRibbonActive = false
}: SearchDropdownProps) => {
  const [deferredIsLoading, setDeferredIsLoading] = useState(isLoading)
  const isInstantResultsActive = dropdownMode === SearchDropdownMode.results

  // Kept this useMemo separate from noResults below so its array references (relied on by the
  // Algolia Insights analytics effect) stay stable across keystrokes and only change
  // when productResults/querySuggestions actually change (i.e. once per debounced fetch).
  const {
    resultsToDisplay,
    querySuggestionsToDisplay,
    isMoreProductsButtonVisible
  } = useMemo(() => {
    const computedResultsToDisplay = productResults
      .filter(result => !isUnavailableProduct(result.nameShort))
      .slice(0, SEARCH_QUERY_INSTANT_RESULTS_DISPLAY_COUNT)
    const computedQuerySuggestionsToDisplay = querySuggestions.slice(
      0,
      SEARCH_QUERY_SUGGESTIONS_DISPLAY_COUNT
    )
    const computedIsMoreProductsButtonVisible =
      productResults.length > SEARCH_QUERY_INSTANT_RESULTS_DISPLAY_COUNT &&
      isInstantResultsActive

    return {
      resultsToDisplay: computedResultsToDisplay,
      querySuggestionsToDisplay: computedQuerySuggestionsToDisplay,
      isMoreProductsButtonVisible: computedIsMoreProductsButtonVisible
    }
  }, [productResults, querySuggestions, isInstantResultsActive])

  const noResults = useMemo(
    () =>
      (productResults.length === 0 || isNilOrEmpty(resultsToDisplay)) &&
      !!query &&
      !isLoading &&
      dropdownMode === SearchDropdownMode.results,
    [productResults, resultsToDisplay, query, isLoading, dropdownMode]
  )

  // Defer switching to loading state to avoid flickering when Algolia response time is almost instant.
  // Skip deferring on initial load.
  useEffect(() => {
    if (dropdownMode === SearchDropdownMode.initial) {
      setDeferredIsLoading(isLoading)

      return
    }

    if (!isLoading) {
      setDeferredIsLoading(false)

      return
    }

    const timeoutId = setTimeout(() => {
      setDeferredIsLoading(true)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [isLoading, dropdownMode])

  // Algolia Insights search dropdown analytics
  useEffect(() => {
    if (!isOpen) return

    const hasProductsResults = isNotNilOrEmpty(resultsToDisplay)
    const hasQuerySuggestionsResults =
      isInstantResultsActive && isNotNilOrEmpty(querySuggestionsToDisplay)

    if (!hasProductsResults && !hasQuerySuggestionsResults) return

    const sendInsights = async () => {
      const userToken = await getCurrentAlgoliaInsightsUserToken(customerId)

      if (hasProductsResults) {
        const productsIds = resultsToDisplay.map(result =>
          getAlgoliaProductID(result)
        )

        const productsViewedData = {
          objectIDs: productsIds,
          positions: Array.from(
            { length: productsIds.length },
            (_, index) => index + 1
          ),
          index: ALGOLIA_PRODUCTS_INDEX_NAME,
          queryID: productsQueryID,
          userToken
        }

        algoliaInsightsHitsViewed(productsViewedData)
      }

      if (hasQuerySuggestionsResults && isInstantResultsActive) {
        const querySuggestionsViewedData = {
          objectIDs: querySuggestionsToDisplay.map(item => item.value),
          positions: querySuggestionsToDisplay.map(item => item.sourcePosition),
          index: ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
          queryID: querySuggestionsToDisplay[0]?.queryID ?? null,
          userToken
        }

        algoliaInsightsHitsViewed(querySuggestionsViewedData)
      }
    }

    void sendInsights()
  }, [
    isOpen,
    resultsToDisplay,
    querySuggestionsToDisplay,
    productsQueryID,
    isInstantResultsActive,
    customerId
  ])

  if (!isOpen) return null

  return (
    <div
      className={clsx(styles.searchDropdownWrapper, {
        [styles.ribbonActive]: isRibbonActive
      })}
    >
      <div
        id='search-results'
        className={clsx(styles.searchDropdownContent, {
          [styles.ribbonActive]: isRibbonActive
        })}
      >
        {noResults ? (
          <SearchDropdownNoResults query={query} />
        ) : (
          <>
            {isNotNilOrEmpty(querySuggestionsToDisplay) && (
              <SearchQuerySuggestions
                querySuggestions={querySuggestionsToDisplay}
                isInstantResultsActive={isInstantResultsActive}
                isLoading={deferredIsLoading}
                customerId={customerId}
              />
            )}
            <ul
              className={styles.searchProductsResultsList}
              data-insights-index={ALGOLIA_PRODUCTS_INDEX_NAME}
            >
              <h3 className={styles.resultsTitle}>
                {isInstantResultsActive ? (
                  <>
                    Znalezione produkty{' '}
                    {!deferredIsLoading && (
                      <span className={styles.resultsCount}>
                        ({productResults.length})
                      </span>
                    )}
                  </>
                ) : (
                  'Popularne produkty'
                )}
              </h3>
              {deferredIsLoading ? (
                <SearchProductsResultsSkeleton />
              ) : (
                resultsToDisplay.map((result, i) => (
                  <li className={styles.searchProductCardWrapper} key={result.id}>
                    <SearchProductCard
                      product={result}
                      idxOnList={i}
                      queryID={productsQueryID}
                      customerId={customerId}
                    />
                  </li>
                ))
              )}
              {isMoreProductsButtonVisible && (
                <div className={styles.searchMoreProductsButtonWrapper}>
                  <Button
                    variant={ButtonVariants.primaryOutline}
                    startIcon={<IconSearch />}
                    onClick={handleSearchSubmit}
                  >
                    Zobacz więcej produktów
                  </Button>
                </div>
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

export default SearchDropdown
